import React, { FormEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Gauge,
  Heart,
  LayoutGrid,
  LayoutList,
  MapPin,
  Maximize2,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  Route,
  Send,
  Share2,
  SlidersHorizontal,
  Smartphone,
  Star,
  Timer,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import {
  JOYSTICK_SESSIONS,
  PAGE_SIZE,
  SESSION_DEVICES,
  createJoystickSession,
  formatClock,
  formatDistance,
  formatPercent,
  formatPlayer,
  formatSpeed,
  isRecent,
  matchesQuery,
  matchesRange,
  pageCount,
  sessionSummary,
  sessionToJson,
  sortSessions,
  type JoystickSession,
  type RoutePoint,
  type SessionChip,
  type SessionRange,
  type SessionSort,
} from '../../data/joystick-mock';
import JoystickArt from './JoystickArt';
import JoystickMap, { type MapStyleName } from './JoystickMap';
import RouteThumb from './RouteThumb';
import { StatsPanels } from './JoystickCharts';
import './joystick.css';

export interface JoystickViewProps {
  /** True while the sessions request is in flight. */
  loading?: boolean;
}

type DialogState =
  | { type: 'create' }
  | { type: 'rename'; id: string }
  | { type: 'delete'; id: string }
  | null;

type DetailTab = 'map' | 'stats' | 'timeline';
type LayoutMode = 'list' | 'grid';
type Pane = 'list' | 'detail';

const RATES = [1, 2, 4];

function isNarrow(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 1079px)').matches;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function coords(point: RoutePoint): string {
  return `${point.latitude.toFixed(4)}, ${point.longitude.toFixed(4)}`;
}

function pointFacts(point: RoutePoint, mode: 'card' | 'timeline'): string {
  const accuracy = `Accuracy: ${point.accuracyM} m`;
  if (point.speedKmh == null) return accuracy;
  const speed = `Speed: ${formatSpeed(point.speedKmh)}`;
  if (mode === 'timeline') return `${speed} · ${accuracy}`;
  return point.kind === 'via' ? speed : accuracy;
}

function pagerItems(page: number, pages: number): Array<number | 'gap'> {
  if (pages <= 6) return Array.from({ length: pages }, (_, index) => index + 1);
  const wanted = [1, pages, page - 2, page - 1, page, page + 1, page + 2].filter((item, index, list) => (
    item >= 1 && item <= pages && list.indexOf(item) === index
  )).sort((a, b) => a - b);
  const items: Array<number | 'gap'> = [];
  wanted.forEach((item, index) => {
    if (index > 0 && item - wanted[index - 1] > 1) items.push('gap');
    items.push(item);
  });
  return items;
}

function downloadJson(filename: string, json: string) {
  try {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch {
    /* Some browsers block the object URL. The toast still confirms the export. */
  }
}

function Skel({ className = '', ...rest }: { className?: string } & React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={`js-skel ${className}`.trim()} {...rest} />;
}

function JoystickSkeleton() {
  return (
    <div className="js js--skeleton" role="status" aria-busy="true">
      <span className="visually-hidden">Loading joystick sessions</span>
      <div className="js-head" data-testid="skeleton-js-header">
        <Skel className="js-skel--btn" />
      </div>
      <div className="js-toolbar" data-testid="skeleton-js-toolbar">
        <Skel className="js-skel--search" />
        <Skel className="js-skel--chips" />
      </div>
      <div className="js-board is-list">
        <div className="js-list-col">
          <div className="js-list" data-testid="skeleton-js-list">
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
          </div>
        </div>
        <div className="js-detail" data-testid="skeleton-js-detail">
          <Skel className="js-skel--line" />
          <Skel className="js-skel--tabs" />
          <Skel className="js-skel--map" data-testid="skeleton-js-map" />
          <div className="js-kpis" data-testid="skeleton-js-kpis">
            <Skel className="js-skel--stat" />
            <Skel className="js-skel--stat" />
            <Skel className="js-skel--stat" />
            <Skel className="js-skel--stat" />
            <Skel className="js-skel--stat" />
          </div>
          <div className="js-split3">
            <Skel className="js-skel--card" data-testid="skeleton-js-points" />
            <Skel className="js-skel--card" data-testid="skeleton-js-summary" />
            <Skel className="js-skel--card" data-testid="skeleton-js-device" />
          </div>
          <div className="js-stats__charts">
            <Skel className="js-skel--chart" data-testid="skeleton-js-speed" />
            <Skel className="js-skel--chart" data-testid="skeleton-js-elevation" />
          </div>
          <div className="js-stats__lower">
            <Skel className="js-skel--chart" data-testid="skeleton-js-distribution" />
            <Skel className="js-skel--chart" data-testid="skeleton-js-movement" />
          </div>
          <div data-testid="skeleton-js-timeline">
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
            <Skel className="js-skel--row" />
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  title,
  text,
  action,
  onAction,
}: {
  title: string;
  text: string;
  action: string;
  onAction: () => void;
}) {
  return (
    <section className="js-empty" data-testid="joystick-empty">
      <JoystickArt />
      <h2>{title}</h2>
      <p>{text}</p>
      <button type="button" className="js-btn js-btn--primary" onClick={onAction}>
        {action}
      </button>
    </section>
  );
}

const JoystickView: React.FC<JoystickViewProps> = ({ loading = false }) => {
  const searchId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);
  const [sessions, setSessions] = useState<JoystickSession[]>(() => [...JOYSTICK_SESSIONS]);
  const [selectedId, setSelectedId] = useState('sunday-morning');
  const [chip, setChip] = useState<SessionChip>('all');
  const [sort, setSort] = useState<SessionSort>('latest');
  const [range, setRange] = useState<SessionRange>('all');
  const [device, setDevice] = useState('all');
  const [query, setQuery] = useState('');
  const [minKm, setMinKm] = useState(0);
  const [fastOnly, setFastOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [layout, setLayout] = useState<LayoutMode>('list');
  const [pane, setPane] = useState<Pane>('list');
  const [tab, setTab] = useState<DetailTab>('map');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [draftName, setDraftName] = useState('');
  const [draftArea, setDraftArea] = useState('Laxmi Nagar, Delhi');
  const [draftDevice, setDraftDevice] = useState(SESSION_DEVICES[0].id);
  const [nameError, setNameError] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [full, setFull] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [mapStyle, setMapStyle] = useState<MapStyleName>('map');
  const [activePoint, setActivePoint] = useState<string | null>(null);

  progressRef.current = progress;

  const base = useMemo(() => sessions.filter((session) => (
    matchesQuery(session, query)
    && matchesRange(session.startedAt, range)
    && (device === 'all' || session.deviceId === device)
    && session.distanceKm >= minKm
    && (!fastOnly || session.avgSpeedKmh >= 80)
  )), [sessions, query, range, device, minKm, fastOnly]);

  const visible = useMemo(() => {
    const chipped = base.filter((session) => {
      if (chip === 'recent') return isRecent(session.startedAt);
      if (chip === 'favourites') return session.favourite;
      if (chip === 'live') return session.status === 'live';
      return true;
    });
    return sortSessions(chipped, sort);
  }, [base, chip, sort]);

  const pages = pageCount(visible.length);
  const safePage = Math.min(page, pages);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const selected = visible.find((session) => session.id === selectedId) ?? visible[0] ?? null;
  const liveCount = base.filter((session) => session.status === 'live').length;
  const filtersOn = minKm > 0 || fastOnly;
  const from = visible.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const to = Math.min(visible.length, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage((current) => Math.min(current, pages));
  }, [pages]);

  useEffect(() => {
    if (typeof listRef.current?.scrollTo === 'function') {
      listRef.current.scrollTo({ top: 0 });
    }
  }, [safePage, chip, sort, query]);

  useEffect(() => {
    setProgress(0);
    setPlaying(false);
    setZoom(1);
    setActivePoint(null);
  }, [selected?.id]);

  const playDuration = selected?.durationSec ?? 0;
  const playId = selected?.id ?? '';

  useEffect(() => {
    if (!playing || !playId) return undefined;
    if (prefersReducedMotion()) {
      setProgress(1);
      setPlaying(false);
      return undefined;
    }
    const fromProgress = progressRef.current >= 0.999 ? 0 : progressRef.current;
    const started = performance.now();
    const durationMs = Math.max(400, (playDuration * 1000) / rate);
    let frame = 0;
    const tick = (now: number) => {
      const next = fromProgress + (now - started) / durationMs;
      if (next >= 1) {
        setProgress(1);
        setPlaying(false);
        return;
      }
      setProgress(next);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [playing, rate, playId, playDuration]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (!menuId && !filtersOpen && !rateOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.js-menu, .js-more, .js-pop, .js-filters, .js-rate')) return;
      setMenuId(null);
      setFiltersOpen(false);
      setRateOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuId(null);
        setFiltersOpen(false);
        setRateOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId, filtersOpen, rateOpen]);

  useEffect(() => {
    if (!dialog && !full) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (dialog) setDialog(null);
      else setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, full]);

  const resetPage = () => setPage(1);

  const openSession = (id: string) => {
    setSelectedId(id);
    setMenuId(null);
    if (isNarrow()) setPane('detail');
  };

  const toggleStar = (id: string) => {
    setSessions((current) => current.map((session) => (
      session.id === id ? { ...session, favourite: !session.favourite } : session
    )));
  };

  const sendSession = (session: JoystickSession) => {
    setToast(`Sent '${session.name}' to ${session.deviceName}.`);
  };

  const exportSession = (session: JoystickSession) => {
    const filename = `${session.name.replace(/[^\w.-]+/g, '-')}.json`;
    downloadJson(filename, sessionToJson(session));
    setToast(`Exported ${session.name}.`);
  };

  const shareSession = async (session: JoystickSession) => {
    try {
      await navigator.clipboard.writeText(sessionSummary(session));
      setToast('Copied session summary.');
    } catch {
      setToast('Could not copy the summary.');
    }
  };

  const openCreate = () => {
    setDraftName('');
    setDraftArea('Laxmi Nagar, Delhi');
    setDraftDevice(SESSION_DEVICES[0].id);
    setNameError('');
    setDialog({ type: 'create' });
  };

  const submitCreate = (event: FormEvent) => {
    event.preventDefault();
    const name = draftName.trim();
    if (!name) {
      setNameError('Enter a session name.');
      return;
    }
    const created = createJoystickSession({ name, area: draftArea, deviceId: draftDevice });
    setSessions((current) => [created, ...current]);
    setSelectedId(created.id);
    setChip('all');
    setQuery('');
    setPage(1);
    setDialog(null);
    setToast(`Created ${created.name}.`);
    if (isNarrow()) setPane('detail');
  };

  const submitRename = (event: FormEvent) => {
    event.preventDefault();
    if (!dialog || dialog.type !== 'rename') return;
    const name = draftName.trim();
    if (!name) {
      setNameError('Enter a session name.');
      return;
    }
    setSessions((current) => current.map((session) => (
      session.id === dialog.id ? { ...session, name } : session
    )));
    setDialog(null);
    setToast('Session renamed.');
  };

  const confirmDelete = () => {
    if (!dialog || dialog.type !== 'delete') return;
    const target = sessions.find((session) => session.id === dialog.id);
    setSessions((current) => current.filter((session) => session.id !== dialog.id));
    setDialog(null);
    setToast(target ? `Deleted ${target.name}.` : 'Session deleted.');
  };

  if (loading) return <JoystickSkeleton />;

  const start = selected?.points.find((point) => point.kind === 'start') ?? null;
  const end = selected?.points.find((point) => point.kind === 'end') ?? null;

  return (
    <div className="js" data-pane={pane}>
      <header className="js-head">
        <button type="button" className="js-btn js-btn--primary" onClick={openCreate}>
          <Plus size={16} aria-hidden="true" />
          New Session
        </button>
      </header>

      <div className="js-toolbar">
        <form className="js-search" role="search" onSubmit={(event) => event.preventDefault()}>
          <MapPin size={16} aria-hidden="true" />
          <label className="visually-hidden" htmlFor={searchId}>Search sessions</label>
          <input
            id={searchId}
            type="search"
            value={query}
            placeholder="Search sessions..."
            onChange={(event) => {
              setQuery(event.target.value);
              resetPage();
            }}
          />
        </form>

        <div className="js-chips" role="group" aria-label="Session filters">
          <button type="button" className="js-chip" aria-pressed={chip === 'all'} onClick={() => { setChip('all'); resetPage(); }}>
            All Sessions
            <span className="js-count">{base.length}</span>
          </button>
          <button type="button" className="js-chip" aria-pressed={chip === 'recent'} onClick={() => { setChip('recent'); resetPage(); }}>
            <Clock size={14} aria-hidden="true" />
            Recent
          </button>
          <button type="button" className="js-chip" aria-pressed={chip === 'favourites'} onClick={() => { setChip('favourites'); resetPage(); }}>
            <Heart size={14} aria-hidden="true" />
            Favourites
          </button>
          <button type="button" className="js-chip" aria-pressed={chip === 'live'} onClick={() => { setChip('live'); resetPage(); }}>
            <span className="js-live-dot" aria-hidden="true" />
            Live
            <span className="js-count">{liveCount}</span>
          </button>
        </div>

        <label className="js-select">
          <span>Sort</span>
          <select
            aria-label="Sort sessions"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value as SessionSort);
              resetPage();
            }}
          >
            <option value="latest">Latest first</option>
            <option value="oldest">Oldest first</option>
            <option value="distance">Longest distance</option>
            <option value="duration">Longest duration</option>
            <option value="speed">Top speed</option>
          </select>
        </label>

        <label className="js-select">
          <select
            aria-label="Time range"
            value={range}
            onChange={(event) => {
              setRange(event.target.value as SessionRange);
              resetPage();
            }}
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
          </select>
        </label>

        <label className="js-select">
          <select
            aria-label="Device"
            value={device}
            onChange={(event) => {
              setDevice(event.target.value);
              resetPage();
            }}
          >
            <option value="all">All devices</option>
            {SESSION_DEVICES.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </label>

        <div className="js-filters">
          <button
            type="button"
            className={`js-btn js-btn--ghost${filtersOn ? ' is-on' : ''}`}
            aria-expanded={filtersOpen}
            onClick={() => {
              setRateOpen(false);
              setMenuId(null);
              setFiltersOpen((open) => !open);
            }}
          >
            <SlidersHorizontal size={15} aria-hidden="true" />
            Filters
            {filtersOn ? <span className="js-filters__dot" aria-hidden="true" /> : null}
          </button>
          {filtersOpen && (
            <div className="js-pop" role="dialog" aria-label="More filters">
              <label className="js-field">
                Minimum distance
                <select value={minKm} onChange={(event) => { setMinKm(Number(event.target.value)); resetPage(); }}>
                  <option value={0}>Any distance</option>
                  <option value={1}>1 km and up</option>
                  <option value={3}>3 km and up</option>
                  <option value={5}>5 km and up</option>
                  <option value={10}>10 km and up</option>
                </select>
              </label>
              <label className="js-check">
                <input
                  type="checkbox"
                  checked={fastOnly}
                  onChange={(event) => {
                    setFastOnly(event.target.checked);
                    resetPage();
                  }}
                />
                Fast runs, 80 km/h average and up
              </label>
            </div>
          )}
        </div>

        <div className="js-layout" role="group" aria-label="Layout">
          <button type="button" aria-label="List view" aria-pressed={layout === 'list'} onClick={() => setLayout('list')}>
            <LayoutList size={16} aria-hidden="true" />
          </button>
          <button type="button" aria-label="Grid view" aria-pressed={layout === 'grid'} onClick={() => setLayout('grid')}>
            <LayoutGrid size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={sessions.length === 0 ? 'No joystick sessions yet' : 'No sessions match'}
          text={sessions.length === 0
            ? 'Record a joystick run in MockLoc and it will show up here.'
            : 'Nothing in this view matches those filters.'}
          action={sessions.length === 0 ? 'New Session' : 'Clear filters'}
          onAction={sessions.length === 0 ? openCreate : () => {
            setQuery('');
            setChip('all');
            setRange('all');
            setDevice('all');
            setMinKm(0);
            setFastOnly(false);
            setPage(1);
          }}
        />
      ) : (
        <div className={`js-board is-${layout}`}>
          <div className="js-list-col">
            <div className="js-list" ref={listRef}>
              {pageItems.map((session) => {
                const active = selected?.id === session.id;
                return (
                  <article
                    key={session.id}
                    data-testid="session-row"
                    className={`js-row${active ? ' is-selected' : ''}`}
                  >
                    <button
                      type="button"
                      className="js-row__open"
                      aria-current={active ? 'true' : undefined}
                      aria-label={`${session.name}, ${session.status === 'live' ? 'Live' : 'Completed'}, ${session.dateLabel}, ${session.area}, ${formatDistance(session.distanceKm)}, ${formatClock(session.durationSec)}`}
                      onClick={() => openSession(session.id)}
                    >
                      <RouteThumb points={session.points} className="js-thumb--card" />
                      <span className="js-row__copy">
                        <span className="js-row__title">
                          <strong>{session.name}</strong>
                          <span className={`js-status js-status--${session.status}`}>
                            {session.status === 'live' ? 'LIVE' : 'Completed'}
                          </span>
                        </span>
                        <span className="js-row__meta">
                          <CalendarDot />
                          {`${session.dateLabel} · ${session.timeLabel}`}
                        </span>
                        <span className="js-row__meta">
                          <MapPin size={13} aria-hidden="true" />
                          {session.area}
                        </span>
                        <span className="js-row__stats">
                          <span><Route size={13} aria-hidden="true" />{formatDistance(session.distanceKm)}</span>
                          <span><Clock size={13} aria-hidden="true" />{formatClock(session.durationSec)}</span>
                        </span>
                      </span>
                    </button>
                    <div className="js-row__tools">
                      <button
                        type="button"
                        className={`js-icon${session.favourite ? ' is-on' : ''}`}
                        aria-label={session.favourite ? `Remove ${session.name} from favourites` : `Favourite ${session.name}`}
                        aria-pressed={session.favourite}
                        onClick={() => toggleStar(session.id)}
                      >
                        <Star size={16} fill={session.favourite ? 'currentColor' : 'none'} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="js-icon js-more"
                        aria-label={`More actions for ${session.name}`}
                        aria-expanded={menuId === session.id}
                        onClick={() => {
                          setFiltersOpen(false);
                          setMenuId((current) => (current === session.id ? null : session.id));
                        }}
                      >
                        <MoreHorizontal size={16} aria-hidden="true" />
                      </button>
                    </div>
                    {menuId === session.id && (
                      <div className="js-menu" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setDraftName(session.name);
                            setNameError('');
                            setMenuId(null);
                            setDialog({ type: 'rename', id: session.id });
                          }}
                        >
                          <Pencil size={14} aria-hidden="true" />
                          Rename
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="is-danger"
                          onClick={() => {
                            setMenuId(null);
                            setDialog({ type: 'delete', id: session.id });
                          }}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                          Delete
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>

          <nav className="js-pager" aria-label="Session pages">
            <p>{`Showing ${from}-${to} of ${visible.length} sessions`}</p>
            <div>
              <button type="button" aria-label="Previous page" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                <ChevronLeft size={16} aria-hidden="true" />
              </button>
              {pagerItems(safePage, pages).map((item, index) => (
                item === 'gap' ? (
                  <span key={`gap-${index}`} className="js-pager__gap">…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    aria-label={`Page ${item}`}
                    aria-current={item === safePage ? 'page' : undefined}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </button>
                )
              ))}
              <button type="button" aria-label="Next page" disabled={safePage >= pages} onClick={() => setPage(safePage + 1)}>
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
          </nav>

          {full && (
            <button type="button" className="js-full-backdrop" aria-label="Exit full screen" onClick={() => setFull(false)} />
          )}
          {selected && (
            <section className={`js-detail${full ? ' is-full' : ''}`} data-testid="session-detail" aria-label="Session detail">
              <button type="button" className="js-back" onClick={() => setPane('list')}>
                <ChevronLeft size={16} aria-hidden="true" />
                All sessions
              </button>
              <header className="js-detail__head">
                <div>
                  <div className="js-detail__title">
                    <h2>{selected.name}</h2>
                    <span className={`js-status js-status--${selected.status}`}>
                      {selected.status === 'live' ? 'LIVE' : 'Completed'}
                    </span>
                    <button
                      type="button"
                      className={`js-icon${selected.favourite ? ' is-on' : ''}`}
                      aria-label={selected.favourite ? `Remove ${selected.name} from favourites` : `Favourite ${selected.name}`}
                      aria-pressed={selected.favourite}
                      onClick={() => toggleStar(selected.id)}
                    >
                      <Star size={16} fill={selected.favourite ? 'currentColor' : 'none'} aria-hidden="true" />
                    </button>
                  </div>
                  <p>{selected.rangeLabel}</p>
                  <p className="js-detail__area">
                    <MapPin size={14} aria-hidden="true" />
                    {selected.area}
                  </p>
                </div>
                <div className="js-actions">
                  <button type="button" className="js-btn js-btn--send" onClick={() => sendSession(selected)}>
                    <Send size={15} aria-hidden="true" />
                    Send to device
                  </button>
                  <button type="button" className="js-btn js-btn--ghost" onClick={() => exportSession(selected)}>
                    <Download size={15} aria-hidden="true" />
                    Export
                  </button>
                  <button type="button" className="js-btn js-btn--ghost" onClick={() => { shareSession(selected).catch(() => setToast('Could not copy the summary.')); }}>
                    <Share2 size={15} aria-hidden="true" />
                    Share
                  </button>
                  <button
                    type="button"
                    className="js-btn js-btn--ghost"
                    aria-pressed={full}
                    onClick={() => setFull((value) => !value)}
                  >
                    <Maximize2 size={15} aria-hidden="true" />
                    {full ? 'Exit full screen' : 'Open in full screen'}
                  </button>
                </div>
              </header>

              <div className="js-tabs" role="tablist" aria-label="Session views">
                {([
                  ['map', 'Map', MapPin],
                  ['stats', 'Stats', BarChart3],
                  ['timeline', 'Timeline', Clock],
                ] as const).map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    id={`js-tab-${id}`}
                    aria-selected={tab === id}
                    aria-controls={`js-panel-${id}`}
                    onClick={() => setTab(id)}
                  >
                    <Icon size={15} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'map' && (
                <div role="tabpanel" id="js-panel-map" aria-labelledby="js-tab-map" className="js-tabpanel">
                  <JoystickMap
                    session={selected}
                    progress={progress}
                    zoom={zoom}
                    styleName={mapStyle}
                    activePointId={activePoint}
                    onZoom={setZoom}
                    onStyle={setMapStyle}
                    onSelectPoint={setActivePoint}
                    onRecenter={() => setZoom(1)}
                  />
                  <PlaybackBar
                    session={selected}
                    progress={progress}
                    playing={playing}
                    rate={rate}
                    rateOpen={rateOpen}
                    onToggle={() => {
                      if (progress >= 0.999) setProgress(0);
                      setPlaying((value) => !value);
                    }}
                    onScrub={(value) => {
                      setPlaying(false);
                      setProgress(value);
                    }}
                    onRate={(next) => {
                      setRate(next);
                      setRateOpen(false);
                    }}
                    onRateOpen={() => setRateOpen((open) => !open)}
                    onFull={() => setFull(true)}
                  />
                  <KpiRow session={selected} />
                  <div className="js-split3">
                    <section className="js-panel">
                      <header className="js-panel__head">
                        <h3>{`Route Points (${selected.points.length})`}</h3>
                      </header>
                      <ul className="js-points">
                        {selected.points.map((point) => (
                          <li key={point.id}>
                            <button
                              type="button"
                              className={point.id === activePoint ? 'is-active' : ''}
                              onClick={() => setActivePoint(point.id)}
                            >
                              <i className={`js-dot js-dot--${point.kind}`} />
                              <span>
                                <b>{point.title}</b>
                                <small>{point.timeLabel}</small>
                                <small>{coords(point)}</small>
                                <small>{pointFacts(point, 'card')}</small>
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                    <section className="js-panel">
                      <header className="js-panel__head">
                        <h3>Route Summary</h3>
                      </header>
                      <RouteThumb points={selected.points} className="js-thumb--summary" />
                      <ul className="js-ends">
                        {start && (
                          <li>
                            <i className="js-dot js-dot--start" />
                            <span>
                              <b>Start Point</b>
                              <small>{coords(start)}</small>
                              <small>{start.place}</small>
                            </span>
                          </li>
                        )}
                        {end && (
                          <li>
                            <i className="js-dot js-dot--end" />
                            <span>
                              <b>End Point</b>
                              <small>{coords(end)}</small>
                              <small>{end.place}</small>
                            </span>
                          </li>
                        )}
                      </ul>
                    </section>
                    <DeviceCard session={selected} onSend={() => sendSession(selected)} onOpen={() => setToast(`Open MockLoc on ${selected.deviceName} to play this session.`)} />
                  </div>
                </div>
              )}

              {tab === 'stats' && (
                <div role="tabpanel" id="js-panel-stats" aria-labelledby="js-tab-stats" className="js-tabpanel">
                  <KpiRow session={selected} featured />
                  <StatsGrid session={selected} />
                  <StatsPanels session={selected} />
                </div>
              )}

              {tab === 'timeline' && (
                <div role="tabpanel" id="js-panel-timeline" aria-labelledby="js-tab-timeline" className="js-tabpanel" data-testid="joystick-timeline">
                  <div className="js-time">
                    <div>
                      <JoystickMap
                        session={selected}
                        progress={progress}
                        zoom={zoom}
                        styleName={mapStyle}
                        activePointId={activePoint}
                        onZoom={setZoom}
                        onStyle={setMapStyle}
                        onSelectPoint={setActivePoint}
                        onRecenter={() => setZoom(1)}
                      />
                      <PlaybackBar
                        session={selected}
                        progress={progress}
                        playing={playing}
                        rate={rate}
                        rateOpen={rateOpen}
                        onToggle={() => {
                          if (progress >= 0.999) setProgress(0);
                          setPlaying((value) => !value);
                        }}
                        onScrub={(value) => {
                          setPlaying(false);
                          setProgress(value);
                        }}
                        onRate={(next) => {
                          setRate(next);
                          setRateOpen(false);
                        }}
                        onRateOpen={() => setRateOpen((open) => !open)}
                        onFull={() => setFull(true)}
                      />
                    </div>
                    <section className="js-panel">
                      <header className="js-panel__head">
                        <h3>Route Timeline</h3>
                        <p>{`${selected.points.length} points`}</p>
                      </header>
                      <ol className="js-time-list">
                        {selected.points.map((point, index) => (
                          <li key={point.id}>
                            <button
                              type="button"
                              className={`js-time-item${point.id === activePoint ? ' is-active' : ''}`}
                              onClick={() => setActivePoint(point.id)}
                            >
                              <span className="js-time-item__rail">
                                <i className={`js-dot js-dot--${point.kind}`} />
                              </span>
                              <span>
                                <b>{point.title}</b>
                                <small>{point.clockLabel}</small>
                                <small>{coords(point)}</small>
                                <small>{point.place}</small>
                                <small>{pointFacts(point, 'timeline')}</small>
                              </span>
                              <RouteThumb points={selected.points} activeIndex={index} className="js-thumb--tiny" />
                            </button>
                          </li>
                        ))}
                      </ol>
                    </section>
                  </div>
                  <TimelineSummary session={selected} />
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {dialog?.type === 'create' && (
        <Modal title="New joystick session" onClose={() => setDialog(null)}>
          <form onSubmit={submitCreate}>
            <p className="js-dialog__lead">Add a sample route to this list. You can play it back, star it, and send it to a device.</p>
            <label className="js-field">
              Session name
              <input value={draftName} onChange={(event) => setDraftName(event.target.value)} autoFocus maxLength={60} />
            </label>
            {nameError && <p className="js-field__error">{nameError}</p>}
            <label className="js-field">
              Area
              <input value={draftArea} onChange={(event) => setDraftArea(event.target.value)} maxLength={80} />
            </label>
            <label className="js-field">
              Device
              <select value={draftDevice} onChange={(event) => setDraftDevice(event.target.value)}>
                {SESSION_DEVICES.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
            <div className="js-dialog__actions">
              <button type="button" className="js-btn js-btn--ghost" onClick={() => setDialog(null)}>Cancel</button>
              <button type="submit" className="js-btn js-btn--primary">Create session</button>
            </div>
          </form>
        </Modal>
      )}

      {dialog?.type === 'rename' && (
        <Modal title="Rename session" onClose={() => setDialog(null)}>
          <form onSubmit={submitRename}>
            <label className="js-field">
              Session name
              <input value={draftName} onChange={(event) => setDraftName(event.target.value)} autoFocus maxLength={60} />
            </label>
            {nameError && <p className="js-field__error">{nameError}</p>}
            <div className="js-dialog__actions">
              <button type="button" className="js-btn js-btn--ghost" onClick={() => setDialog(null)}>Cancel</button>
              <button type="submit" className="js-btn js-btn--primary">Save name</button>
            </div>
          </form>
        </Modal>
      )}

      {dialog?.type === 'delete' && (
        <Modal title="Delete session" onClose={() => setDialog(null)}>
          <p className="js-dialog__lead">Delete this session from the list?</p>
          <div className="js-dialog__actions">
            <button type="button" className="js-btn js-btn--ghost" onClick={() => setDialog(null)}>Cancel</button>
            <button type="button" className="js-btn js-btn--danger" onClick={confirmDelete}>Delete</button>
          </div>
        </Modal>
      )}

      <div className="js-toasts" aria-live="polite">
        {toast && <p className="js-toast">{toast}</p>}
      </div>
    </div>
  );
};

function CalendarDot() {
  return <Clock size={13} aria-hidden="true" />;
}

function KpiRow({ session, featured = false }: { session: JoystickSession; featured?: boolean }) {
  const items = [
    { icon: Route, label: 'Distance', value: formatDistance(session.distanceKm), tone: 'blue' },
    { icon: Clock, label: 'Duration', value: formatClock(session.durationSec), tone: 'purple' },
    { icon: Gauge, label: 'Avg. Speed', value: formatSpeed(session.avgSpeedKmh), tone: 'indigo' },
    { icon: Zap, label: 'Max. Speed', value: formatSpeed(session.maxSpeedKmh), tone: 'red' },
    { icon: MapPin, label: 'Total Points', value: String(session.points.length), tone: 'violet' },
  ];
  return (
    <div className={`js-kpis${featured ? ' js-kpis--featured' : ''}`}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <article key={item.label} className="js-kpi">
            <span className={`js-ico js-ico--${item.tone}`}><Icon size={16} aria-hidden="true" /></span>
            <span>
              <small>{item.label}</small>
              <strong>{item.value}</strong>
            </span>
          </article>
        );
      })}
    </div>
  );
}

function StatsGrid({ session }: { session: JoystickSession }) {
  const total = session.durationSec || 1;
  const items = [
    { icon: Play, label: 'Moving Time', value: formatClock(session.movingSec), note: formatPercent(session.movingSec, total), tone: 'green' },
    { icon: Pause, label: 'Stationary Time', value: formatClock(session.stationarySec), note: formatPercent(session.stationarySec, total), tone: 'purple' },
    { icon: MapPin, label: 'GPS Accuracy', value: `${session.gpsAccuracyM} meters`, note: '(avg)', tone: 'blue' },
    { icon: MapPin, label: 'Total Points', value: String(session.points.length), note: 'Recorded locations', tone: 'violet' },
    { icon: Timer, label: 'Update interval', value: `${session.updateIntervalSec} seconds`, note: '(avg)', tone: 'cyan' },
  ];
  return (
    <div className="js-metrics">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <article key={item.label} className="js-kpi">
            <span className={`js-ico js-ico--${item.tone}`}><Icon size={16} aria-hidden="true" /></span>
            <span>
              <small>{item.label}</small>
              <strong>{item.value}</strong>
              <em>{item.note}</em>
            </span>
          </article>
        );
      })}
    </div>
  );
}

function DeviceCard({
  session,
  onSend,
  onOpen,
}: {
  session: JoystickSession;
  onSend: () => void;
  onOpen: () => void;
}) {
  return (
    <section className="js-panel js-device">
      <header className="js-panel__head">
        <h3>Device & Playback</h3>
      </header>
      <p className="js-device__lead">Send to Android device</p>
      <p className="js-device__copy">Open this session in the MockLoc app on your connected device.</p>
      <button type="button" className="js-btn js-btn--primary js-btn--wide" onClick={onOpen}>
        <Send size={15} aria-hidden="true" />
        Open on phone
      </button>
      <div className="js-device__row">
        <span className="js-ico js-ico--violet"><Smartphone size={16} aria-hidden="true" /></span>
        <span>
          <b>{session.deviceName}</b>
          <small className={session.deviceOnline ? 'is-on' : ''}>
            {session.deviceOnline ? 'Online' : 'Offline'}
          </small>
        </span>
      </div>
      <p className="js-device__seen">{`Last seen: ${session.lastSeenLabel}`}</p>
      <button type="button" className="js-btn js-btn--send js-btn--wide" aria-label="Send session to connected device" onClick={onSend}>
        Send to device
      </button>
    </section>
  );
}

function TimelineSummary({ session }: { session: JoystickSession }) {
  const clockSpan = session.rangeLabel.split('·')[1]?.replace(/\s*\([^)]*\)\s*$/, '').trim() || session.timeLabel;
  const items = [
    ['Total Points', String(session.points.length), 'Recorded locations'],
    ['Total Distance', formatDistance(session.distanceKm), session.area],
    ['Total Duration', formatClock(session.durationSec), clockSpan],
    ['Avg. Speed', formatSpeed(session.avgSpeedKmh), ''],
    ['Max. Speed', formatSpeed(session.maxSpeedKmh), ''],
  ] as const;
  return (
    <div className="js-summary">
      {items.map(([label, value, note]) => (
        <article key={label}>
          <small>{label}</small>
          <strong>{value}</strong>
          {note ? <em>{note}</em> : null}
        </article>
      ))}
    </div>
  );
}

function PlaybackBar({
  session,
  progress,
  playing,
  rate,
  rateOpen,
  onToggle,
  onScrub,
  onRate,
  onRateOpen,
  onFull,
}: {
  session: JoystickSession;
  progress: number;
  playing: boolean;
  rate: number;
  rateOpen: boolean;
  onToggle: () => void;
  onScrub: (value: number) => void;
  onRate: (rate: number) => void;
  onRateOpen: () => void;
  onFull: () => void;
}) {
  return (
    <div className="js-player">
      <button type="button" className="js-play" aria-label={playing ? 'Pause playback' : 'Play playback'} onClick={onToggle}>
        {playing ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
      </button>
      <span className="js-player__time">
        {`${formatPlayer(progress * session.durationSec)} / ${formatPlayer(session.durationSec)}`}
      </span>
      <input
        type="range"
        min={0}
        max={1000}
        value={Math.round(progress * 1000)}
        aria-label="Playback position"
        onChange={(event) => onScrub(Number(event.target.value) / 1000)}
      />
      <div className="js-rate">
        <button type="button" aria-label={`Playback speed ${rate}x`} aria-expanded={rateOpen} onClick={onRateOpen}>
          {`${rate}x`}
        </button>
        {rateOpen && (
          <div className="js-pop js-pop--up" role="menu" aria-label="Playback speed">
            {RATES.map((item) => (
              <button key={item} type="button" role="menuitemradio" aria-checked={item === rate} onClick={() => onRate(item)}>
                {`${item}x`}
              </button>
            ))}
          </div>
        )}
      </div>
      <button type="button" className="js-icon" aria-label="Expand playback" onClick={onFull}>
        <Maximize2 size={15} aria-hidden="true" />
      </button>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="js-modal">
      <button type="button" className="js-modal__backdrop" aria-label="Close dialog" onClick={onClose} />
      <div className="js-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button type="button" className="js-icon" aria-label="Close" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

export default JoystickView;
